import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { ChangesDetection } from "../../../../change-detection";
import { I_OptionItemResponse } from '../../../../unit/items/master-lists/api/option-list.response';
import { I_PatientResponse } from "../../patient/api/patient.response";
import { C_PatientLocation } from "../../patient/domain/patient-location.model";
import { C_PatientData } from "../../patient/domain/patient.model";
import { I_DischargeResponse } from "../api/discharge.response";
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";

export class C_DischargeList extends ChangesDetection<T_DischargeListChange> {

    private list: Map<string, C_DischargeItem> = new Map();
    private cachedSubs = new Map<string, Observable<C_DischargeItem | undefined>>();

    constructor(json?: I_DischargeResponse) {
        super();
        if (!json) return;
        this.addNewDischarge(json);
    }

    getValues(): C_DischargeItem[] {
        return Array.from(this.list.values()) ?? [];
    }

    size() {
        return this.list.size;
    }

    getDischarge(id: string): C_DischargeItem | undefined {
        return this.list.get(id);
    }

    addNewDischarge(dis: I_DischargeResponse) {
        if (!dis.id) return;
        if (this.list.has(dis.id)) {
            this.list.get(dis.id)!.initializeFromJson(dis);
            this.forceUpdateDetection({
                action: E_ChangeAction.Update,
                dischargeId: dis.id,
                patientId: dis.patientId,
                data: dis
            });
        } else {
            const newDischarge = new C_DischargeItem(dis);
            this.list.set(dis.id, newDischarge);
            this.forceUpdateDetection({
                action: E_ChangeAction.Add,
                dischargeId: dis.id,
                patientId: dis.patientId,
                data: newDischarge
            });
        }
    }

    deleteDischarge(dis: I_DischargeResponse) {
        if (!dis.id) return;
        if (this.list.has(dis.id)) {
            this.list.delete(dis.id);
            this.forceUpdateDetection({
                action: E_ChangeAction.Delete,
                dischargeId: dis.id,
                patientId: dis.patientId
            });
        }
    }

    /**
     * Actualiza el paciente de forma masiva controlada mediante Set de afectados
     */
    updatePatient(pat: I_PatientResponse) {
        if (!pat.id) return;
        const affected = new Set<string>();

        this.list.forEach((item, id) => {
            if (item.patient?.id === pat.id || item.patientId === pat.id) {
                item.updatePatient(pat);
                affected.add(id);
            }
        });

        if (affected.size > 0) {
            this.forceUpdateDetection({ action: E_ChangeAction.UpdateBulk, affected });
        }
    }

    /**
     * Elimina la relación del paciente de forma masiva controlada mediante Set de afectados
     */
    deletePatient(patId: string) {
        if (!patId) return;
        const affected = new Set<string>();

        this.list.forEach((item, id) => {
            if (item.patient?.id === patId || item.patientId === patId) {
                item.patient = undefined;
                item.patientId = undefined;
                item.forceUpdateDetection();
                affected.add(id);
            }
        });

        if (affected.size > 1) {
            this.forceUpdateDetection({ action: E_ChangeAction.DeleteBulk, affected });
        } else if (affected.size === 1) {
            this.forceUpdateDetection({ action: E_ChangeAction.Delete, dischargeId: Array.from(affected.values())[0] });
        }
    }

    resetDischarge() {
        this.list.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }


    /**
     * Canal reactivo granular por ID de alta médica
     */
    getLiveDischarge(dischargeId: string): Observable<C_DischargeItem | undefined> {
        const cacheKey = dischargeId;
        if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

        const stream$ = this.getForcedChanges().pipe(
            startWith(null),
            filter((change) => {
                if (!change) return true; // Carga inicial
                if (change.action === E_ChangeAction.Reset) return true;
                if (change.action === E_ChangeAction.AddBulk || change.action === E_ChangeAction.UpdateBulk || change.action === E_ChangeAction.DeleteBulk) return change.affected.has(dischargeId);

                return change.dischargeId === dischargeId;
            }),
            map(() => this.list.get(dischargeId)),
            distinctUntilChanged(),
            switchMap(item => {
                if (!item) return of(undefined);

                // Escucha de mutaciones directas de la instancia del ítem
                return item.getForcedChanges().pipe(
                    startWith(item),
                    map(() => item)
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.cachedSubs.set(cacheKey, stream$);
        return stream$;
    }

    clearSubs() {
        this.cachedSubs.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }

}

export class C_DischargeItem extends ChangesDetection {
    id?: string;
    dischargeDate?: string;
    pointOfCareId?: string;
    patientId?: string
    destination?: string;
    service?: string;
    medicalDischarge?: string;
    adminDischarge?: string;
    nurseDischarge?: string;
    patient?: C_PatientData;
    patientLocation?: C_PatientLocation
    destinationOption?: I_OptionItemResponse;
    serviceOption?: I_OptionItemResponse;
    unitId?: string;
    class?: string;


    constructor(json?: I_DischargeResponse) {
        super();
        if (!json) return;
        this.initializeFromJson(json);
    }

    initializeFromJson(json?: I_DischargeResponse) {
        if (!json) return;
        if (json.id) this.id = json.id;
        if (json.dischargeDate) this.dischargeDate = json.dischargeDate;
        if (json.nurseDischarge) this.nurseDischarge = json.nurseDischarge;
        if (json.patientLocation) this.patientLocation = new C_PatientLocation(json.patientLocation);
        if (json.patientId) this.patientId = json.patientId;
        if (json.destination) this.destination = json.destination;
        if (json.service) this.service = json.service;
        if (json.medicalDischarge) this.medicalDischarge = json.medicalDischarge;
        if (json.adminDischarge) this.adminDischarge = json.adminDischarge;
        if (json.unitId) this.unitId = json.unitId;
        if (json.patient) this.patient = new C_PatientData(json.patient);
        if (json.pointOfCareId) this.pointOfCareId = json.pointOfCareId;
        if (json.destinationOption) this.destinationOption = json.destinationOption;
        if (json.serviceOption) this.serviceOption = json.serviceOption;
        this.forceUpdateDetection();
    }

    updatePatient(pat: I_PatientResponse) {
        this.patient?.initializeFromJson(pat);
        this.forceUpdateDetection();
    }

    cloneWithoutChangesDetection(): I_DischargeResponse {
        return {
            id: this.id!,
            dischargeDate: this.dischargeDate!,
            pointOfCareId: this.pointOfCareId!,
            patientId: this.patientId!,
            destination: this.destination!,
            service: this.service!,
            medicalDischarge: this.medicalDischarge!,
            adminDischarge: this.adminDischarge!,
            nurseDischarge: this.nurseDischarge!,
            patient: this.patient?.toPatientResponse(),
            patientLocation: this.patientLocation!,
            destinationOption: this.destinationOption!,
            serviceOption: this.serviceOption!,
            unitId: this.unitId!
        };
    }
}

export type T_DischargeListChange =
    | { action: E_ChangeAction.Add; dischargeId: string; patientId?: string; data: C_DischargeItem }
    | { action: E_ChangeAction.Update; dischargeId: string; patientId?: string; data: I_DischargeResponse }
    | { action: E_ChangeAction.Delete; dischargeId: string; patientId?: string }
    | { action: E_ChangeAction.AddBulk; affected: Set<string> }
    | { action: E_ChangeAction.DeleteBulk; affected: Set<string> }
    | { action: E_ChangeAction.UpdateBulk; affected: Set<string> }
    | { action: E_ChangeAction.Reset };